import { SagaIterator } from '@redux-saga/core';
import { all, put, takeEvery, call } from 'redux-saga/effects';

import { authActions } from '../../actions/auth/auth.actions';
import { AuthActionTypes } from '../../actions/auth/auth.types';
import { authService } from '../../services/auth/auth.service';
import { isCustomError, User } from '../../types/auth.types';

function* handleLogin(action: ReturnType<typeof authActions.login>): SagaIterator {
  try {
    const { username, password } = action.payload;
    const user: User = yield call(authService.login, {
      username,
      password,
    });

    yield put(authActions.loginSuccess(user));
  } catch (error) {
    const errorMessage = isCustomError(error) ? error.data?.message || 'Login failed' : 'Login failed';

    yield put(authActions.loginError(errorMessage));
  }
}

function* handleLogout(): SagaIterator {
  try {
    yield call(authService.logout);
    yield put(authActions.logoutSuccess());
  } catch (error) {
    const errorMessage = isCustomError(error) ? error.data?.message || 'Logout failed' : 'Logout failed';

    yield put(authActions.logoutError(errorMessage));
  }
}

function* handleSignup(action: ReturnType<typeof authActions.signup>): SagaIterator {
  try {
    const userData = action.payload;
    yield call(authService.signup, userData);
    yield put(authActions.signupSuccess());
  } catch (error) {
    const errorMessage = isCustomError(error) ? error.data?.message || 'Signup failed' : 'Signup failed';

    yield put(authActions.signupError(errorMessage));
  }
}

function* handleForgotPassword(action: ReturnType<typeof authActions.forgotPassword>): SagaIterator {
  try {
    const { email } = action.payload;
    yield call(authService.forgotPassword, email);
    yield put(authActions.forgotPasswordSuccess());
  } catch (error) {
    const errorMessage = isCustomError(error)
      ? error.data?.message || 'Password reset failed'
      : 'Password reset failed';

    yield put(authActions.forgotPasswordError(errorMessage));
  }
}

function* handleResetPassword(action: ReturnType<typeof authActions.resetPassword>): SagaIterator {
  try {
    const resetData = action.payload;
    yield call(authService.resetPassword, resetData);
    yield put(authActions.resetPasswordSuccess());
  } catch (error) {
    const errorMessage = isCustomError(error)
      ? error.data?.message || 'Password reset failed'
      : 'Password reset failed';

    yield put(authActions.resetPasswordError(errorMessage));
  }
}

export default function* AuthSaga(): SagaIterator {
  yield all([
    takeEvery(AuthActionTypes.LOGIN_USER, handleLogin),
    takeEvery(AuthActionTypes.LOGOUT_USER, handleLogout),
    takeEvery(AuthActionTypes.SIGNUP_USER, handleSignup),
    takeEvery(AuthActionTypes.FORGOT_PASSWORD, handleForgotPassword),
    takeEvery(AuthActionTypes.FORGOT_PASSWORD_CHANGE, handleResetPassword),
  ]);
}
